/**
 * ChatComposer — chat input surface, post-ADR-001 (Tailwind v4 + Radix UI).
 *
 *   - Native `<textarea>` styled with Tailwind utility classes.
 *   - Send button: pill-style, Forest moss accent, `lucide-react` ArrowUp icon.
 *   - Stop-generation button (visible while streaming): same pill, neutral surface.
 *   - Slash-command Autosuggest: a Radix `Popover` anchored to the textarea.
 *     Each row is a Tailwind-styled `<button>` with the slash command in
 *     mono font and a description in `text-wayne-ink-3`.
 *   - `@AgentName` mention completion — typing `@` opens a small list of the
 *     9 agents; Enter accepts the highlighted one and closes the list.
 *   - 4 pre-input agent suggestion pills (Developer, ProjectManager,
 *     DevOps, Security) below the input — clicking prepends `@AgentName ` to
 *     the input, matching the editorial mockup at /tmp/wayne-mocks/02-anthropic.
 *   - Enter to send, Shift+Enter for newline, Esc to clear focus or close
 *     the suggestion list.
 *   - Disabled state while `isStreaming === true`.
 *
 * The `useSlashCommands` hook is untouched — only the rendering changes.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ArrowUp, Paperclip, X } from 'lucide-react';
import {
  useSlashCommands,
  type SlashSuggestion,
} from '@/hooks/useSlashCommands';
import { AGENTS, type AgentId } from '@/tokens/colors';
import {
  parseSlashCommand,
  type ParsedSlashCommand,
} from '@/lib/parseSlashCommand';
import TokenBudgetBar from '@/components/molecules/TokenBudgetBar';

// ---------------------------------------------------------------------------
// File attachment types
// ---------------------------------------------------------------------------

export interface AttachmentMeta {
  readonly name: string;
  readonly size: number;
  readonly key: string;
}

type AttachStatus = 'uploading' | 'done' | 'error';

interface AttachedFile {
  name: string;
  size: number;
  key: string;
  status: AttachStatus;
  error?: string;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// MIME prefixes that are always accepted regardless of extension.
const ALLOWED_MIME_PREFIXES = [
  'text/',
  'application/json',
  'application/pdf',
  'image/',
  // CSV and spreadsheets — browsers/OS report these inconsistently
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml',
  'application/csv',
];

// File extensions accepted when the browser reports an empty MIME type
// (common for .csv on macOS/Windows) or a generic octet-stream.
const ALLOWED_EXTENSIONS = new Set([
  'txt', 'csv', 'tsv', 'log', 'md', 'xml', 'yaml', 'yml',
  'json', 'pdf',
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
  'xls', 'xlsx',
]);

function isFileAllowed(file: File): boolean {
  const mime = file.type;
  if (mime && mime !== 'application/octet-stream') {
    return ALLOWED_MIME_PREFIXES.some((p) => mime.startsWith(p));
  }
  // Empty or generic MIME — fall back to extension check
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ALLOWED_EXTENSIONS.has(ext);
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface ChatComposerProps {
  readonly isStreaming: boolean;
  readonly disabled?: boolean;
  readonly activeAgents?: readonly AgentId[];
  readonly onSend: (text: string, agent?: AgentId, attachments?: readonly AttachmentMeta[]) => void;
  readonly onStop: () => void;
  /** Previously-uploaded files for this session — shown in the tray so the
   *  user can re-attach without re-uploading. */
  readonly sessionFiles?: readonly AttachmentMeta[];
  /** Called when a new upload completes so the parent can persist the key. */
  readonly onFileUploaded?: (file: AttachmentMeta) => void;
  /** Called when the user submits a `/full-auto` or `/auto` slash command.
   *  When provided, recognized FULL_AUTO commands bypass the regular
   *  `onSend` path so the parent can dispatch a `tasks.create` POST with
   *  `confirmedFullAuto: true, source: 'slash'`. */
  readonly onFullAutoSubmit?: (parsed: ParsedSlashCommand) => void;
  /**
   * Phase 5 - Task 13: when set, recognized `/full-auto` and `/auto`
   * slash submissions are short-circuited and the supplied user-facing
   * `message` is rendered inline as an alert instead of calling
   * `onFullAutoSubmit`. The parent computes this from
   * `useFullAutoFlag` so the AppConfig allowedUsers/allowedPackages/env
   * gate matches the backend.
   */
  readonly fullAutoBlocked?: { readonly reason: string; readonly message: string };
}

interface MentionMatch {
  readonly query: string;
  /** Cursor index where the `@` started, so we can replace it cleanly. */
  readonly atIndex: number;
}

/**
 * Find an `@…` token at the end of the current line up to the cursor.
 * Returns null if no mention is in flight.
 */
function findMention(value: string, cursor: number): MentionMatch | null {
  // Look backwards from cursor for `@`. Must be at start-of-line or after
  // whitespace.
  let i = cursor - 1;
  while (i >= 0) {
    const ch = value.charAt(i);
    if (ch === '@') {
      const before = i === 0 ? '' : value.charAt(i - 1);
      if (before === '' || /\s/.test(before)) {
        const query = value.slice(i + 1, cursor);
        if (/^[a-z-]*$/i.test(query)) {
          return { query, atIndex: i };
        }
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
}

const AGENT_LIST: readonly AgentId[] = [
  'developer',
  'manager',
  'project-manager',
  'qa-testing',
  'devops',
  'security',
  'data-engineer',
  'support-engineer',
  'analyst',
];

/** Pre-input quick pick pills — the 4 agents most-frequently @-mentioned. */
const QUICK_AGENTS: readonly AgentId[] = [
  'developer',
  'project-manager',
  'devops',
  'security',
];

export default function ChatComposer({
  isStreaming,
  disabled = false,
  activeAgents = [],
  onSend,
  onStop,
  sessionFiles = [],
  onFileUploaded,
  onFullAutoSubmit,
  fullAutoBlocked,
}: ChatComposerProps): ReactElement {
  // Phase 5 - Task 13: when the AppConfig flag denies full-auto, the
  // composer surfaces an inline alert and refuses to fire onFullAutoSubmit.
  const [fullAutoAlert, setFullAutoAlert] = useState<string | null>(null);
  const activeAgentSet = useMemo(() => new Set(activeAgents), [activeAgents]);
  const [value, setValue] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [cursor, setCursor] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // -- File attachment state --------------------------------------------------
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [attachError, setAttachError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setAttachError('');

    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        setAttachError(`"${file.name}" exceeds the 10 MB size limit.`);
        continue;
      }
      if (!isFileAllowed(file)) {
        setAttachError(`"${file.name}" has an unsupported type.`);
        continue;
      }

      const placeholder: AttachedFile = { name: file.name, size: file.size, key: '', status: 'uploading' };
      setAttachedFiles((prev) => [...prev, placeholder]);

      try {
        const presignRes = await fetch('/api/files/upload', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name }),
        });
        if (!presignRes.ok) throw new Error(`Presign ${presignRes.status}`);
        const { uploadUrl, key } = (await presignRes.json()) as { uploadUrl: string; key: string };

        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!putRes.ok) throw new Error(`Upload ${putRes.status}`);

        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.name === file.name && f.status === 'uploading' ? { ...f, key, status: 'done' } : f,
          ),
        );
        // Register with the session-level file store so it's available for
        // re-attachment in follow-up messages without re-uploading.
        onFileUploaded?.({ name: file.name, size: file.size, key });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.name === file.name && f.status === 'uploading' ? { ...f, status: 'error', error: msg } : f,
          ),
        );
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onFileUploaded]);

  const removeAttachment = useCallback((name: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.name !== name));
  }, []);

  // Re-attach a file that was already uploaded in this session — adds it
  // directly as 'done' without a new S3 upload.
  const reAttachSessionFile = useCallback((file: AttachmentMeta) => {
    setAttachedFiles((prev) => {
      if (prev.some((f) => f.key === file.key)) return prev; // already queued
      return [...prev, { ...file, status: 'done' }];
    });
  }, []);

  const { suggestions, invalid: slashInvalid } = useSlashCommands(value);

  // Mention dropdown — open whenever `findMention` returns a match.
  const mention = useMemo(
    () => findMention(value, cursor),
    [value, cursor],
  );
  const mentionMatches = useMemo<readonly AgentId[]>(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return AGENT_LIST.filter((id) => {
      const display = AGENTS[id]?.displayName.toLowerCase() ?? id;
      return id.toLowerCase().startsWith(q) || display.startsWith(q);
    });
  }, [mention]);

  const showSlash = value.startsWith('/') && suggestions.length > 0;
  const showMention = mention !== null && mentionMatches.length > 0;
  const showDropdown = showSlash || showMention;

  // Reset highlighted-row index when the dropdown contents change.
  const dropdownLength = showSlash
    ? suggestions.length
    : showMention
      ? mentionMatches.length
      : 0;
  useEffect(() => {
    if (activeIndex >= dropdownLength && activeIndex !== 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, dropdownLength]);

  const doneAttachments = attachedFiles.filter((f) => f.status === 'done');
  const hasUploading = attachedFiles.some((f) => f.status === 'uploading');
  const isEmpty = value.trim().length === 0 && doneAttachments.length === 0;
  const sendDisabled = disabled || isEmpty || slashInvalid || isStreaming || hasUploading;

  const handleSend = useCallback((): void => {
    const trimmed = value.trim();
    // FULL_AUTO routing: /full-auto and /auto are first-class commands NOT
    // registered in useSlashCommands (which would mark them invalid). They
    // bypass the regular chat send path and dispatch via onFullAutoSubmit so
    // the parent can POST tasks.create with confirmedFullAuto=true,
    // source='slash'. Run this BEFORE the sendDisabled early-return.
    if (onFullAutoSubmit && !isStreaming && !disabled) {
      const parsed = parseSlashCommand(trimmed);
      if (parsed && (parsed.command === 'full-auto' || parsed.command === 'auto')) {
        // Phase 5 - Task 13 — short-circuit when the AppConfig flag is
        // closed for this user/package/env. We surface the parent-
        // supplied message inline so the user sees the same copy the
        // backend would have streamed back.
        if (fullAutoBlocked) {
          setFullAutoAlert(fullAutoBlocked.message);
          return;
        }
        setFullAutoAlert(null);
        onFullAutoSubmit(parsed);
        setValue('');
        setActiveIndex(0);
        setCursor(0);
        setAttachedFiles([]);
        setAttachError('');
        return;
      }
    }
    if (sendDisabled) return;
    // Allow send with attachments and no text
    if (trimmed.length === 0 && doneAttachments.length === 0) return;
    let agent: AgentId | undefined;
    let outgoing = trimmed;
    const m = /^@([A-Za-z][A-Za-z -]*?)\s{2,}|^@([A-Za-z][A-Za-z-]*)\s+/.exec(trimmed);
    const candidate = (m?.[2] ?? m?.[1] ?? '').toLowerCase().trim();
    if (candidate) {
      const matched = AGENT_LIST.find((id) => {
        const display = AGENTS[id]?.displayName.toLowerCase() ?? id;
        return id === candidate || display === candidate || display.replace(/\s+/g, '-') === candidate;
      });
      if (matched) {
        agent = matched;
        outgoing = trimmed.slice(m![0].length);
      }
    }
    const metas: AttachmentMeta[] = doneAttachments.map(({ name, size, key }) => ({ name, size, key }));
    onSend(outgoing, agent, metas.length > 0 ? metas : undefined);
    setValue('');
    setActiveIndex(0);
    setCursor(0);
    setAttachedFiles([]);
    setAttachError('');
  }, [onSend, sendDisabled, value, doneAttachments, onFullAutoSubmit, isStreaming, disabled, fullAutoBlocked]);

  const acceptSuggestion = useCallback(
    (s: SlashSuggestion): void => {
      setValue(s.fill);
      setCursor(s.fill.length);
      setActiveIndex(0);
      // Restore focus to the textarea after the popover row click.
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    [],
  );

  const acceptMention = useCallback(
    (agent: AgentId): void => {
      if (!mention) return;
      const display = AGENTS[agent]?.displayName ?? agent;
      const before = value.slice(0, mention.atIndex);
      const afterStart = mention.atIndex + 1 + mention.query.length;
      const after = value.slice(afterStart);
      const replacement = `@${display} `;
      const next = `${before}${replacement}${after}`;
      setValue(next);
      setCursor(before.length + replacement.length);
      setActiveIndex(0);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    [mention, value],
  );

  const handleQuickAgent = useCallback(
    (agent: AgentId): void => {
      const prefix = `@${agent} `;
      setValue((cur) => (cur.startsWith(prefix) ? cur : `${prefix}${cur}`));
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    [],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTextAreaElement>): void => {
      const { key, shiftKey } = event;
      // Shift+Enter inserts a newline (default textarea behaviour).
      if (key === 'Enter' && !shiftKey) {
        // If a dropdown row is highlighted, accept the suggestion instead of sending.
        if (showSlash) {
          event.preventDefault();
          const picked = suggestions[activeIndex];
          if (picked) acceptSuggestion(picked);
          return;
        }
        if (showMention) {
          event.preventDefault();
          const picked = mentionMatches[activeIndex];
          if (picked) acceptMention(picked);
          return;
        }
        event.preventDefault();
        handleSend();
        return;
      }
      if (key === 'Escape') {
        if (showDropdown) {
          event.preventDefault();
          setActiveIndex(0);
          if (showMention && mention) {
            const next =
              value.slice(0, mention.atIndex) +
              value.slice(mention.atIndex + 1 + mention.query.length);
            setValue(next);
            setCursor(mention.atIndex);
          }
        }
        return;
      }
      if (key === 'ArrowDown' && showDropdown) {
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % Math.max(1, dropdownLength));
        return;
      }
      if (key === 'ArrowUp' && showDropdown) {
        event.preventDefault();
        setActiveIndex((i) =>
          (i - 1 + Math.max(1, dropdownLength)) % Math.max(1, dropdownLength),
        );
        return;
      }
    },
    [
      acceptMention,
      acceptSuggestion,
      activeIndex,
      dropdownLength,
      handleSend,
      mention,
      mentionMatches,
      showDropdown,
      showMention,
      showSlash,
      suggestions,
      value,
    ],
  );

  const placeholder = isStreaming
    ? 'Streaming…'
    : 'Ask Wayne — `/` for commands, `@` to direct an agent';

  const showSlashError = slashInvalid && value.startsWith('/');

  return (
    <div data-testid="chat-composer" className="relative">
      {/* Hidden native file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="text/*,application/json,application/pdf,image/*,.csv,.tsv,.xls,.xlsx,.yaml,.yml,.log,.md"
        className="hidden"
        onChange={handleFileSelect}
        aria-hidden="true"
      />

      {/* Attachment error */}
      {attachError && (
        <div
          role="alert"
          className="mb-2 text-[12px] px-3 py-1.5 rounded-md bg-wayne-surface border border-wayne-hairline"
          style={{ color: 'var(--wayne-red)' }}
        >
          {attachError}
          <button
            type="button"
            onClick={() => setAttachError('')}
            className="ml-2 underline"
            aria-label="Dismiss"
          >
            dismiss
          </button>
        </div>
      )}

      {/* Phase 5 - Task 13: AppConfig full-auto gate alert */}
      {fullAutoAlert && (
        <div
          role="alert"
          aria-label="Full-auto is not enabled"
          className="mb-2 text-[12px] px-3 py-1.5 rounded-md bg-wayne-surface border border-wayne-hairline"
          style={{ color: 'var(--wayne-red)' }}
        >
          {fullAutoAlert}
          <button
            type="button"
            onClick={() => setFullAutoAlert(null)}
            className="ml-2 underline"
            aria-label="Dismiss full-auto alert"
          >
            dismiss
          </button>
        </div>
      )}

      {/* Session files tray — files uploaded earlier in this conversation */}
      {sessionFiles.length > 0 && (
        <div className="mb-2">
          <p className="text-[11px] text-wayne-ink-3 mb-1">Files in this session — click to re-attach:</p>
          <div className="flex flex-wrap gap-1.5">
            {sessionFiles.map((f) => {
              const alreadyAttached = attachedFiles.some((a) => a.key === f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => reAttachSessionFile(f)}
                  disabled={alreadyAttached}
                  title={alreadyAttached ? 'Already attached' : `Re-attach ${f.name}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-wayne-hairline bg-wayne-surface text-wayne-ink-2 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ fontSize: 11 }}
                >
                  <Paperclip size={10} aria-hidden="true" />
                  <span className="max-w-[140px] truncate">{f.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active attachment chips */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" aria-label="Attached files">
          {attachedFiles.map((f) => (
            <span
              key={f.name}
              title={f.status === 'error' ? f.error : f.name}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-wayne-hairline bg-wayne-surface text-wayne-ink-2 max-w-[200px]"
              style={{ fontSize: 11 }}
            >
              {f.status === 'uploading' && (
                <span
                  aria-label="uploading"
                  className="w-2 h-2 rounded-full border border-current border-t-transparent animate-spin shrink-0"
                />
              )}
              {f.status === 'done' && (
                <span aria-label="uploaded" className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
              )}
              {f.status === 'error' && (
                <span aria-label="error" className="w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--wayne-red)' }} />
              )}
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => removeAttachment(f.name)}
                aria-label={`Remove ${f.name}`}
                className="shrink-0 ml-0.5 hover:text-wayne-ink transition-colors"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Token budget progress bar — stub: tokensUsed=0 until finalize plumbing lands */}
      <TokenBudgetBar tokensUsed={0} hardCap={200_000} />

      <Popover.Root open={showDropdown} modal={false}>
        <Popover.Anchor asChild>
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={value}
              placeholder={placeholder}
              rows={3}
              disabled={disabled}
              data-testid="chat-composer-input"
              aria-label="Message Wayne"
              onChange={(e) => {
                setValue(e.target.value);
                setCursor(e.target.selectionStart ?? e.target.value.length);
              }}
              onSelect={(e) => {
                const t = e.currentTarget;
                setCursor(t.selectionStart ?? t.value.length);
              }}
              onKeyDown={onKeyDown}
              className="w-full bg-wayne-surface border border-wayne-hairline rounded-lg px-4 py-3 text-[14px] text-wayne-ink placeholder:text-wayne-ink-3 focus:outline-none focus:border-wayne-accent resize-none min-h-[68px] disabled:opacity-60"
            />
          </div>
        </Popover.Anchor>
        <Popover.Portal>
          <Popover.Content
            side="top"
            align="start"
            sideOffset={6}
            // Don't steal focus from the textarea — let the user keep typing.
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            // Force-keep open: it's controlled by `showDropdown`.
            onInteractOutside={(e) => e.preventDefault()}
            role="listbox"
            aria-label={showSlash ? 'Slash commands' : 'Direct to agent'}
            className="bg-wayne-surface border border-wayne-hairline rounded-lg shadow-md p-1 text-[13px] min-w-[300px] max-h-[240px] overflow-y-auto z-50"
          >
            {showSlash
              ? suggestions.map((s, i) => (
                  <button
                    key={s.command}
                    type="button"
                    role="option"
                    aria-selected={i === activeIndex}
                    onMouseDown={(e) => {
                      // mousedown so the textarea doesn't lose focus before
                      // the click registers.
                      e.preventDefault();
                      acceptSuggestion(s);
                    }}
                    className={[
                      'w-full px-3 py-2 rounded-md cursor-pointer flex items-center gap-2 text-left transition-colors',
                      i === activeIndex
                        ? 'bg-wayne-surface-2'
                        : 'hover:bg-wayne-surface-2',
                    ].join(' ')}
                  >
                    <span className="font-mono text-wayne-ink shrink-0">
                      {s.command}
                    </span>
                    {s.args ? (
                      <span className="font-mono text-wayne-ink-3 shrink-0">
                        {s.args}
                      </span>
                    ) : null}
                    <span className="text-wayne-ink-3 truncate flex-1 min-w-0">
                      {s.description}
                    </span>
                    {s.validation === 'invalid' ? (
                      <span
                        className="font-medium shrink-0"
                        style={{ color: 'var(--wayne-red)' }}
                        aria-hidden="true"
                      >
                        ✕
                      </span>
                    ) : null}
                  </button>
                ))
              : mentionMatches.map((id, i) => {
                  const display = AGENTS[id]?.displayName ?? id;
                  const code = AGENTS[id]?.code ?? '';
                  const isActive = activeAgentSet.has(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      role="option"
                      aria-selected={i === activeIndex}
                      aria-disabled={isActive}
                      disabled={isActive}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (!isActive) acceptMention(id);
                      }}
                      className={[
                        'w-full px-3 py-2 rounded-md flex items-center gap-2 text-left transition-colors',
                        isActive
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer',
                        !isActive && i === activeIndex
                          ? 'bg-wayne-surface-2'
                          : !isActive
                            ? 'hover:bg-wayne-surface-2'
                            : '',
                      ].join(' ')}
                    >
                      <span
                        aria-hidden="true"
                        className="rounded-full shrink-0"
                        style={{
                          width: 6,
                          height: 6,
                          background: `var(--agent-${id})`,
                        }}
                      />
                      <span className="font-medium text-wayne-ink">
                        {display}
                      </span>
                      {isActive ? (
                        <span className="text-wayne-ink-3 ml-1" style={{ fontSize: 10 }}>
                          (added)
                        </span>
                      ) : null}
                      <span
                        className="font-mono text-wayne-ink-3 ml-auto"
                        style={{ fontSize: 11 }}
                      >
                        {code}
                      </span>
                    </button>
                  );
                })}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {showSlashError ? (
        <div
          role="alert"
          className="mt-1 text-[12px]"
          style={{ color: 'var(--wayne-red)' }}
        >
          Slash command argument is missing or invalid.
        </div>
      ) : null}

      {/* Quick-pick agent pills + attach + send buttons row */}
      <div className="flex items-center justify-between gap-2 mt-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Attach button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isStreaming}
            aria-label="Attach file"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-wayne-hairline bg-wayne-surface text-wayne-ink-2 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontSize: 11, fontWeight: 500 }}
          >
            <Paperclip size={12} aria-hidden="true" />
            Attach
          </button>
          {QUICK_AGENTS.map((id) => {
            const display = AGENTS[id]?.displayName ?? id;
            const isActive = activeAgentSet.has(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => !isActive && handleQuickAgent(id)}
                disabled={isActive}
                className={[
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-wayne-hairline bg-wayne-surface transition-colors',
                  isActive
                    ? 'opacity-50 cursor-not-allowed text-wayne-ink-3'
                    : 'text-wayne-ink-2 hover:text-wayne-ink hover:bg-wayne-surface-2',
                ].join(' ')}
                style={{ fontSize: 11, fontWeight: 500 }}
                aria-label={isActive ? `${display} already added` : `Direct message to ${display}`}
              >
                <span
                  aria-hidden="true"
                  className="rounded-full shrink-0"
                  style={{
                    width: 6,
                    height: 6,
                    background: `var(--agent-${id})`,
                  }}
                />
                {display}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isStreaming ? (
            <button
              type="button"
              onClick={onStop}
              aria-label="Stop generation"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-wayne-surface border border-wayne-hairline text-wayne-ink hover:bg-wayne-surface-2 transition-colors"
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              Stop
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSend}
            disabled={sendDisabled}
            data-testid="chat-composer-send"
            aria-label="Send message"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-wayne-accent text-white hover:bg-wayne-accent-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-wayne-accent"
            style={{ fontSize: 13, fontWeight: 500 }}
          >
            <ArrowUp size={16} aria-hidden="true" />
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
